import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';
import { TableRowSkeleton } from '../../components/admin/TableRowSkeleton';

// Hook para Debounce
function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

// Types
interface GiftCard {
    id: number;
    code: string;
    initial_balance: number | string;
    is_redeemed: boolean;
    redeemed_by_username?: string;
    redeemed_at?: string;
    expires_at?: string;
}

const initialGiftCardState = { initial_balance: undefined, expires_at: '' };

export default function AdminGiftCardsPage() {
    const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [newGiftCard, setNewGiftCard] = useState<{ initial_balance?: number; expires_at?: string }>(initialGiftCardState);
    const [formStatus, setFormStatus] = useState({ loading: false, error: '' });

    const { showNotification } = useNotificationDispatch();
    const limit = 20;
    const totalPages = Math.ceil(totalCount / limit);

    const fetchGiftCards = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/admin/giftcards', {
                params: { page: currentPage, search: debouncedSearchTerm }
            });
            setGiftCards(response.data.giftCards);
            setTotalCount(response.data.totalCount);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao buscar vales-presente.', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm, showNotification]);

    useEffect(() => {
        fetchGiftCards();
    }, [fetchGiftCards]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormStatus({ loading: true, error: '' });

        try {
            await apiClient.post('/admin/giftcards', newGiftCard);
            showNotification('Vale-presente criado com sucesso!', 'success');
            setNewGiftCard(initialGiftCardState);
            await fetchGiftCards();
        } catch (err: any) {
            setFormStatus({ loading: false, error: err.response?.data?.error || 'Ocorreu um erro.' });
        } finally {
            setFormStatus(prev => ({ ...prev, loading: false }));
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Tem certeza que deseja remover este vale-presente?')) {
            try {
                await apiClient.delete(`/admin/giftcards/${id}`);
                showNotification('Vale-presente removido com sucesso!', 'success');
                fetchGiftCards();
            } catch (err: any) {
                showNotification(err.response?.data?.error || 'Falha ao remover o vale-presente.', 'error');
            }
        }
    };

    const handleCopy = (code: string) => {
        navigator.clipboard.writeText(code);
        showNotification('Código copiado para a área de transferência!', 'success');
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewGiftCard(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold text-text-primary">Gerenciar Gift Cards</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                    <div className="bg-white p-6 rounded-lg shadow-subtle sticky lg:top-20">
                        <h2 className="text-xl font-bold text-text-primary mb-4 border-b pb-3">Criar Novo Vale-Presente</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="form-label">Saldo Inicial (R$)</label>
                                <input type="number" name="initial_balance" value={newGiftCard.initial_balance || ''} onChange={handleChange} required className="form-input" placeholder="Ex: 50.00" step="0.01" />
                            </div>
                            <div>
                                <label className="form-label">Data de Expiração (Opcional)</label>
                                <input type="datetime-local" name="expires_at" value={newGiftCard.expires_at || ''} onChange={handleChange} className="form-input" />
                            </div>
                            {formStatus.error && <p className="text-red-500 text-sm">{formStatus.error}</p>}
                            <button type="submit" disabled={formStatus.loading} className="w-full bg-brand-blue text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400">
                                {formStatus.loading ? 'Criando...' : 'Criar Vale-Presente'}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold text-text-primary">Vales-Presente Criados ({totalCount})</h2>
                        <input 
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar por código..."
                            className="form-input w-full max-w-xs"
                        />
                    </div>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full table-fixed">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="th-cell w-1/6">Código</th>
                                        <th className="th-cell w-1/6">Saldo</th>
                                        <th className="th-cell w-1/6">Status</th>
                                        <th className="th-cell w-1/4">Resgatado por</th>
                                        <th className="th-cell w-1/4">Data do Resgate</th>
                                        <th className="th-cell w-1/6">Expira em</th>
                                        <th className="th-cell w-1/12">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {loading ? (
                                        Array.from({ length: limit }).map((_, i) => <TableRowSkeleton key={i} columns={7} />)
                                    ) : (
                                        giftCards.map(card => (
                                            <tr key={card.id}>
                                                <td className="td-cell">
                                                    <button onClick={() => handleCopy(card.code)} className="flex items-center gap-2 text-sm font-medium text-brand-blue hover:underline">
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                                        Copiar
                                                    </button>
                                                </td>
                                                <td className="td-cell">R$ {parseFloat(String(card.initial_balance)).toFixed(2)}</td>
                                                <td className="td-cell">
                                                    {card.is_redeemed ? <span className="badge-blue">Resgatado</span> : <span className="badge-gray">Disponível</span>}
                                                </td>
                                                <td className="td-cell truncate">{card.redeemed_by_username || '-'}</td>
                                                <td className="td-cell">{card.redeemed_at ? new Date(card.redeemed_at).toLocaleString('pt-BR') : '-'}</td>
                                                <td className="td-cell">{card.expires_at ? new Date(card.expires_at).toLocaleDateString('pt-BR') : 'Nunca'}</td>
                                                <td className="td-cell">
                                                    <button onClick={() => handleDelete(card.id)} className="text-red-500 hover:text-red-700">
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-4">
                            <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                Anterior
                            </button>
                            <span className="text-sm text-gray-700">Página {currentPage} de {totalPages}</span>
                            <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                Próxima
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
